import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Play, Send, Clock, MessageSquare, Loader2, RefreshCw } from "lucide-react";

export default function MarkdownCampaignsPage() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/marketing/markdown-settings"),
        api.get("/marketing/markdown-history"),
      ]);
      setSettings(s.data);
      setHistory(h.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load markdown data");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {
        ...settings,
        discount_tiers: (settings.discount_tiers || []).map((t) => ({
          max_days: Number(t.max_days),
          percent: Number(t.percent),
        })),
        days_ahead: Number(settings.days_ahead),
        target_recent_days: Number(settings.target_recent_days),
        schedule_hour: Number(settings.schedule_hour),
        schedule_minute: Number(settings.schedule_minute),
        max_per_batch: Number(settings.max_per_batch),
        flat_discount_pct: settings.flat_discount_pct === "" || settings.flat_discount_pct == null
          ? null : Number(settings.flat_discount_pct),
      };
      const res = await api.put("/marketing/markdown-settings", clean);
      setSettings(res.data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async (dryRun = false) => {
    setRunning(true);
    try {
      const res = await api.post("/marketing/markdown-run", { dry_run: dryRun });
      toast.success(
        dryRun
          ? `Preview: ${res.data.batches_with_targets} batches would target customers`
          : `Sent ${res.data.total_sent} · Failed ${res.data.total_failed}`,
      );
      await load();
      if (!dryRun && res.data?.id) {
        const detail = await api.get(`/marketing/markdown-history/${res.data.id}`);
        setSelectedRun(detail.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const previewCampaign = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const res = await api.get("/marketing/markdown-preview");
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const openRun = async (id) => {
    try {
      const res = await api.get(`/marketing/markdown-history/${id}`);
      setSelectedRun(res.data);
    } catch (e) {
      toast.error("Failed to load run");
    }
  };

  const addTier = () => {
    setSettings((s) => ({ ...s, discount_tiers: [...(s.discount_tiers || []), { max_days: 14, percent: 10 }] }));
  };
  const removeTier = (idx) => {
    setSettings((s) => ({ ...s, discount_tiers: (s.discount_tiers || []).filter((_, i) => i !== idx) }));
  };
  const updateTier = (idx, key, val) => {
    setSettings((s) => ({
      ...s,
      discount_tiers: (s.discount_tiers || []).map((t, i) => (i === idx ? { ...t, [key]: val } : t)),
    }));
  };

  if (!settings) {
    return (
      <div className="p-8 flex items-center justify-center text-[#8B7355]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading markdown campaigns…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="markdown-campaigns-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-[Outfit] font-semibold text-[#2C1810] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#C05621]" />
            Near-Expiry Markdown Campaigns
          </h1>
          <p className="text-sm text-[#8B7355] mt-1">
            Recover revenue on near-expiring stock by auto-blasting WhatsApp discount offers to previous buyers.
            Runs daily at your chosen time · or on demand.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={previewCampaign} data-testid="markdown-preview-btn">
            <MessageSquare className="w-4 h-4 mr-1" /> Preview matches
          </Button>
          <Button
            className="bg-[#C05621] hover:bg-[#9C4221] text-white"
            onClick={() => runNow(false)}
            disabled={running}
            data-testid="markdown-run-now-btn"
          >
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Run Now
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 border-[#EAE5D9]">
          <CardHeader>
            <CardTitle className="font-[Outfit] text-lg flex items-center gap-2">
              <Clock className="w-4 h-4 text-[#C05621]" /> Campaign Settings
            </CardTitle>
            <CardDescription>Tune what triggers a markdown and who gets it.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between rounded-md bg-[#FAF6EE] border border-[#EAE5D9] px-4 py-3">
              <div>
                <div className="font-medium">Auto-run daily</div>
                <div className="text-xs text-[#8B7355]">
                  Scheduled at {String(settings.schedule_hour).padStart(2, "0")}:
                  {String(settings.schedule_minute).padStart(2, "0")} IST
                </div>
              </div>
              <Switch
                checked={!!settings.enabled}
                onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
                data-testid="markdown-enabled-switch"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Look ahead (days)</Label>
                <Input
                  type="number"
                  value={settings.days_ahead}
                  onChange={(e) => setSettings({ ...settings, days_ahead: e.target.value })}
                  data-testid="markdown-days-ahead"
                />
              </div>
              <div>
                <Label>Target — customers who bought this product in last (days)</Label>
                <Input
                  type="number"
                  value={settings.target_recent_days}
                  onChange={(e) => setSettings({ ...settings, target_recent_days: e.target.value })}
                  data-testid="markdown-target-recent-days"
                />
              </div>
              <div>
                <Label>Schedule hour (24h, IST)</Label>
                <Input
                  type="number" min="0" max="23"
                  value={settings.schedule_hour}
                  onChange={(e) => setSettings({ ...settings, schedule_hour: e.target.value })}
                  data-testid="markdown-schedule-hour"
                />
                <div className="text-[10px] text-[#8B7355] mt-1">Changes take effect after next backend restart.</div>
              </div>
              <div>
                <Label>Schedule minute</Label>
                <Input
                  type="number" min="0" max="59"
                  value={settings.schedule_minute}
                  onChange={(e) => setSettings({ ...settings, schedule_minute: e.target.value })}
                  data-testid="markdown-schedule-minute"
                />
              </div>
              <div>
                <Label>Flat discount % (leave blank for tiered)</Label>
                <Input
                  type="number" step="0.1"
                  placeholder="e.g. 20"
                  value={settings.flat_discount_pct ?? ""}
                  onChange={(e) => setSettings({ ...settings, flat_discount_pct: e.target.value })}
                  data-testid="markdown-flat-pct"
                />
              </div>
              <div>
                <Label>Max sends per batch</Label>
                <Input
                  type="number"
                  value={settings.max_per_batch}
                  onChange={(e) => setSettings({ ...settings, max_per_batch: e.target.value })}
                  data-testid="markdown-max-per-batch"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={!!settings.include_all_customers}
                onCheckedChange={(v) => setSettings({ ...settings, include_all_customers: v })}
                data-testid="markdown-include-all-switch"
              />
              <div>
                <div className="text-sm font-medium">Also blast to all customers with phone numbers</div>
                <div className="text-xs text-[#8B7355]">Dedup guaranteed — buyers of the product are always included first.</div>
              </div>
            </div>

            <div>
              <Label className="flex items-center justify-between">
                <span>Discount tiers (used when flat % is blank)</span>
                <button onClick={addTier} className="text-xs text-[#C05621] hover:underline" data-testid="markdown-add-tier">
                  + add tier
                </button>
              </Label>
              <div className="mt-2 space-y-2">
                {(settings.discount_tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                    <div>
                      <div className="text-[10px] text-[#8B7355]">If ≤ this many days to expiry</div>
                      <Input
                        type="number"
                        value={t.max_days}
                        onChange={(e) => updateTier(i, "max_days", e.target.value)}
                        data-testid={`tier-days-${i}`}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] text-[#8B7355]">Discount %</div>
                      <Input
                        type="number" step="0.1"
                        value={t.percent}
                        onChange={(e) => updateTier(i, "percent", e.target.value)}
                        data-testid={`tier-pct-${i}`}
                      />
                    </div>
                    <button
                      onClick={() => removeTier(i)}
                      className="text-xs text-red-600 hover:underline pt-4"
                      data-testid={`tier-remove-${i}`}
                    >
                      remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>WhatsApp message template</Label>
              <Textarea
                rows={7}
                value={settings.message_template}
                onChange={(e) => setSettings({ ...settings, message_template: e.target.value })}
                data-testid="markdown-template"
              />
              <div className="text-[10px] text-[#8B7355] mt-1">
                Placeholders: {"{customer_name}, {product_name}, {batch_no}, {expiry_date}, {discount_pct}, {days_left}, {company_name}"}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#2C1810] hover:bg-[#3E2822] text-white"
                data-testid="markdown-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Save settings
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#EAE5D9]">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-[Outfit] text-lg flex items-center gap-2">
                <Send className="w-4 h-4 text-[#C05621]" /> Recent runs
              </CardTitle>
              <CardDescription>Last 30 executions.</CardDescription>
            </div>
            <button onClick={load} className="text-xs text-[#C05621] hover:underline flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-[#EAE5D9] max-h-[600px] overflow-y-auto" data-testid="markdown-history-list">
              {history.length === 0 && (
                <div className="p-6 text-center text-sm text-[#8B7355]">No runs yet. Try “Run Now”.</div>
              )}
              {history.map((r) => (
                <button
                  key={r.id}
                  onClick={() => openRun(r.id)}
                  className="w-full text-left px-4 py-3 hover:bg-[#FAF6EE]"
                  data-testid={`markdown-run-${r.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-[#8B7355]">
                      {new Date(r.started_at).toLocaleString("en-IN")}
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      {r.trigger_type}
                    </Badge>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-medium text-emerald-700">{r.total_sent} sent</span>
                    {r.total_failed > 0 && <span className="text-red-600"> · {r.total_failed} failed</span>}
                    <span className="text-[#8B7355]"> · {r.batches_with_targets} of {r.batches_scanned} batches</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!selectedRun} onOpenChange={(o) => !o && setSelectedRun(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Campaign Run</DialogTitle>
            <DialogDescription>
              {selectedRun && new Date(selectedRun.started_at).toLocaleString("en-IN")}
              {selectedRun?.dry_run ? " · DRY RUN" : ""}
            </DialogDescription>
          </DialogHeader>
          {selectedRun && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-3">
                <StatBox label="Batches scanned" value={selectedRun.batches_scanned} />
                <StatBox label="Batches sent" value={selectedRun.batches_with_targets} />
                <StatBox label="Total sent" value={selectedRun.total_sent} tone="green" />
                <StatBox label="Failed" value={selectedRun.total_failed} tone={selectedRun.total_failed ? "red" : "gray"} />
              </div>
              {(selectedRun.batches || []).length === 0 && (
                <div className="text-center py-8 text-[#8B7355]">No batches matched.</div>
              )}
              {(selectedRun.batches || []).map((b) => (
                <div key={b.batch_id} className="border border-[#EAE5D9] rounded-md p-3" data-testid={`run-batch-${b.batch_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{b.product_name} <span className="text-[#8B7355] text-xs">· {b.batch_no}</span></div>
                      <div className="text-xs text-[#8B7355]">
                        Expiry {b.expiry_date} · {b.days_left} day(s) left · Discount {b.discount_pct}%
                      </div>
                    </div>
                    <Badge className="bg-[#C05621] text-white">{b.sends?.length || 0} customers</Badge>
                  </div>
                  {(b.sends || []).length > 0 && (
                    <div className="mt-2 text-xs bg-[#FAF6EE] rounded p-2 max-h-40 overflow-y-auto">
                      {b.sends.map((s, idx) => (
                        <div key={idx} className="flex items-center justify-between py-0.5">
                          <span>{s.customer_name} · {s.phone}</span>
                          <Badge
                            variant="outline"
                            className={
                              s.status === "sent" ? "text-emerald-700 border-emerald-500" :
                              s.status === "would_send" ? "text-blue-700 border-blue-400" :
                              "text-red-700 border-red-400"
                            }
                          >
                            {s.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedRun(null)} data-testid="run-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) { setPreviewOpen(false); setPreview(null); } }}>
        <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Outfit]">Preview matches (dry run)</DialogTitle>
            <DialogDescription>Nothing is sent. Shows what today's job would target with current settings.</DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="p-8 text-center text-[#8B7355]"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>
          ) : preview ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <StatBox label="Batches scanned" value={preview.batches_scanned} />
                <StatBox label="Batches with targets" value={preview.batches_with_targets} />
                <StatBox label="Would be sent" value={(preview.batches || []).reduce((n, b) => n + (b.sends?.length || 0), 0)} tone="green" />
              </div>
              {(preview.batches || []).map((b) => (
                <div key={b.batch_id} className="border border-[#EAE5D9] rounded p-2 text-xs">
                  <div className="font-medium">{b.product_name} · {b.batch_no}</div>
                  <div className="text-[#8B7355]">Expiry {b.expiry_date} · {b.days_left}d · Discount {b.discount_pct}% · {b.sends?.length || 0} customers</div>
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, tone = "default" }) {
  const toneCls =
    tone === "green" ? "text-emerald-700" :
    tone === "red" ? "text-red-700" :
    tone === "gray" ? "text-[#8B7355]" :
    "text-[#2C1810]";
  return (
    <div className="border border-[#EAE5D9] rounded-md p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-[#8B7355]">{label}</div>
      <div className={`text-2xl font-[Outfit] font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}
